import {
  Alert,
  Box,
  Button,
  Container,
  IconButton,
  Stack,
  TextField,
  Typography,
  Popover,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Tooltip,
} from "@mui/material";
import React, { useCallback, useEffect, useState } from "react";
import useGetEmailTemplate from "../../hooks/useGetEmailTemplate";
import MuPb from "../../widgets/MuPb";
import { getAuthHeader, getErrorResponse, toastError } from "../../utils/util";
import FullImageModal from "../../widgets/FullImageModal";
import MyQuill from "../../quill/MyQuill";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import AddIcon from "@mui/icons-material/Add";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { LoadingButton } from "@mui/lab";
import ImageCropperModal from "../../widgets/ImageCropperModal";
import { API_ENDPOINT } from "../../utils/constants";
import { toast } from "react-toastify";
import axios from "axios";
import { useParams, useSearchParams } from "react-router-dom";
import BackBtn from "../../widgets/BackBtn";

const variables = ["emailId", "lastName", "firstName", "companyName", "qrCode"];

const EmailTemplate = () => {
  const { id } = useParams();
  const [searchParam] = useSearchParams();
  const type = searchParam.get("type"); // can be empty, 'si', or 'tdVisitor'
  const { data, isLoading, isError, error, refetch } = useGetEmailTemplate(
    id,
    type
  );

  const [imageToShow, setImageToShow] = useState("");
  const [cropperOpen, setCropperOpen] = useState(false);
  const [propertyType, setPropertyType] = useState("");
  const [aspectRatio, setAspectRatio] = useState(2 / 1);
  const [anchorEl, setAnchorEl] = useState(null);
  const [showImage, setShowImage] = useState(false);

  const [emailBody, setEmailBody] = useState("");
  const [senderEmail, setSenderEmail] = useState("");
  const [emailSubject, setEmailSubject] = useState("");

  const [savingBody, setSavingBody] = useState(false);

  // Determine prefix for image uploads based on type
  const templatePrefix =
    type === "si"
      ? "siEmailTemplate"
      : type === "tdVisitor"
      ? "tdVisitorEmailTemplate"
      : "emailTemplate";

  useEffect(() => {
    // Backend get route maps all default/si/tdVisitor to 'emailTemplate' for frontend consumption
    if (data?.emailTemplate) {
      setEmailBody(data.emailTemplate.emailBody || "");
      setEmailSubject(data.emailTemplate.subject || "");
      setSenderEmail(data.emailTemplate.senderEmail || "");
    }
  }, [data]);

  const handleEmailSave = useCallback(async () => {
    // Validation check for required fields
    const isBodyEmpty =
      !emailBody || emailBody === "<p><br></p>" || emailBody.trim() === "";
    if (!senderEmail || !emailSubject || isBodyEmpty) {
      toast.error("Sender Email, Subject, and Email Body are required.");
      return;
    }

    try {
      setSavingBody(true);
      // Append query parameter dynamically if type exists
      const endpoint = type
        ? `${API_ENDPOINT}event/update-email-body?type=${type}`
        : `${API_ENDPOINT}event/update-email-body`;

      const response = await axios.post(
        endpoint,
        { emailBody, emailSubject, senderEmail },
        getAuthHeader(id)
      );
      toast.success(response.data.message);
      refetch();
    } catch (error) {
      toastError(error);
    } finally {
      setSavingBody(false);
    }
  }, [emailBody, emailSubject, senderEmail, id, refetch, type]);

  if (isLoading) {
    return <MuPb />;
  }

  if (isError) {
    return <Alert severity="error">{getErrorResponse(error)}</Alert>;
  }

  const handleCloseImage = () => setShowImage(false);

  const handleShowImage = (imgUrl) => {
    setImageToShow(imgUrl);
    setShowImage(true);
  };

  const handleOpen = (event) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const handleCopy = (variable) => {
    navigator.clipboard.writeText(`{{${variable}}}`);
    handleClose();
    toast.success(`Copied {{${variable}}}`);
  };

  const handleUploadSuccess = (response) => {
    toast.success(response.message);
    refetch();
  };

  return (
    <Container maxWidth="md" sx={{ mt: 2, pb: 4 }}>
      <BackBtn />
      <Typography my={2} variant="h6">
        Update Email Template <b>{data?.eventName}</b>
      </Typography>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
        {/* Header Image Section */}
        <Box sx={{ position: "relative", width: "100%" }}>
          <img
            style={{ width: "100%", borderRadius: "8px", objectFit: "cover" }}
            src={data?.emailTemplate?.headerUrl}
            alt="Email Banner Header"
            onClick={() => handleShowImage(data?.emailTemplate?.headerUrl)}
          />
          <Button
            onClick={() => {
              setPropertyType(
                `${templatePrefix}.headerUrl${type ? `?type=${type}` : ""}`
              );
              setAspectRatio(1198 / 199);
              setCropperOpen(true);
            }}
            variant="outlined"
            startIcon={<AddIcon />}
            size="small"
            sx={{
              position: "absolute",
              bottom: 8,
              right: 8,
              backgroundColor: "white",
              "&:hover": { backgroundColor: "rgba(255, 255, 255, 0.9)" },
            }}
          >
            Change Header
          </Button>
        </Box>

        {/* Content Section */}
        <Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 2,
            }}
          >
            <Typography variant="h6">Update your email content</Typography>
            <Button variant="outlined" onClick={handleOpen} size="small">
              Variables to use
            </Button>

            {/* Popover Menu */}
            <Popover
              open={Boolean(anchorEl)}
              anchorEl={anchorEl}
              onClose={handleClose}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
            >
              <List dense>
                {variables.map((variable) => (
                  <ListItem key={variable} disablePadding>
                    <ListItemButton onClick={() => handleCopy(variable)}>
                      <ListItemText primary={`{{${variable}}}`} />
                      <Tooltip title="Copy">
                        <IconButton edge="end">
                          <ContentCopyIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            </Popover>
          </Box>

          {/* Form Fields Stack */}
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            sx={{ mb: 2 }}
          >
            <TextField
              fullWidth
              size="small"
              variant="outlined"
              label="Sender Email *"
              placeholder="e.g. hello@event.com"
              value={senderEmail}
              onChange={(event) => setSenderEmail(event.target.value)}
            />
            <TextField
              fullWidth
              size="small"
              variant="outlined"
              label="Email Subject *"
              placeholder="Enter your email subject"
              value={emailSubject}
              onChange={(event) => setEmailSubject(event.target.value)}
            />
          </Stack>

          {/* Quill Editor */}
          <Box sx={{ mb: 2 }}>
            <MyQuill value={emailBody} onChange={setEmailBody} />
          </Box>

          <Stack direction="row" justifyContent="flex-end">
            <LoadingButton
              variant="contained"
              disableElevation
              loading={savingBody}
              onClick={handleEmailSave}
              startIcon={<SaveOutlinedIcon fontSize="small" />}
            >
              Save Content
            </LoadingButton>
          </Stack>
        </Box>

        {/* Footer Image Section */}
        <Box sx={{ position: "relative", width: "100%" }}>
          <img
            style={{ width: "100%", borderRadius: "8px", objectFit: "cover" }}
            src={data?.emailTemplate?.footerUrl}
            alt="Email Banner Footer"
            onClick={() => handleShowImage(data?.emailTemplate?.footerUrl)}
          />
          <Button
            onClick={() => {
              setPropertyType(
                `${templatePrefix}.footerUrl${type ? `?type=${type}` : ""}`
              );
              setAspectRatio(1198 / 199);
              setCropperOpen(true);
            }}
            variant="outlined"
            startIcon={<AddIcon />}
            size="small"
            sx={{
              position: "absolute",
              bottom: 8,
              right: 8,
              backgroundColor: "white",
              "&:hover": { backgroundColor: "rgba(255, 255, 255, 0.9)" },
            }}
          >
            Change Footer
          </Button>
        </Box>
      </Box>

      {/* Modals */}
      <FullImageModal
        imgUrl={imageToShow}
        open={showImage}
        handleClose={handleCloseImage}
      />
      <ImageCropperModal
        open={cropperOpen}
        type={propertyType}
        onClose={() => setCropperOpen(false)}
        aspectRatio={aspectRatio}
        maxSizeKB={1024}
        uploadUrl={`${API_ENDPOINT}event/upload-image-form-email-template/${propertyType}`}
        headers={{
          Authorization: getAuthHeader(id).headers.Authorization,
        }}
        onSuccess={handleUploadSuccess}
        onError={(error) => console.error("Upload error:", error)}
      />
    </Container>
  );
};

export default EmailTemplate;
